import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Feature flags for copilot v2 components
 * These flags control which features are enabled/disabled at runtime
 */
export interface CopilotFeatureFlags {
    /**
     * Enable the new CopilotPromptSlider component in zero query state
     * When true: Uses carousel-based slider with swipe support
     * When false: Uses original PromptStarterList component
     */
    usePromptSlider: boolean;

    /**
     * Enable CST Web Chat component (CSTCopilotV2) from cst-web-chat package
     * When true: Uses CSTCopilotV2 component with standardized API
     * When false: Uses the current custom Copilot implementation
     */
    useCSTWebChat: boolean;

    /**
     * Enable the grounding menu for @mentions (actions, people, views)
     */
    isGroundingMenuEnabled: boolean;

    /**
     * Enable the prompt gallery/library feature
     */
    isPromptGalleryEnabled: boolean;

    /**
     * Enable the feedback modal for user feedback collection
     */
    isFeedbackModalEnabled: boolean;

    /**
     * Enable the floating action button (FAB) for copilot
     */
    isFloatingActionButtonEnabled: boolean;

    /**
     * Enable pinned/docked mode for the copilot panel
     */
    isPinnedModeEnabled: boolean;

    /**
     * Enable typewriting animation effect for bot responses
     */
    isTypewritingEnabled: boolean;

    /**
     * Enable slash commands (e.g., /help, /clear)
     */
    isSlashCommandEnabled: boolean;

    /**
     * Enable auto suggestions based on context
     */
    isAutoSuggestionEnabled: boolean;

    /**
     * Enable the legacy V2 Copilot implementation (copilot-v2-legacy)
     * When true: Uses the copilot-v2-legacy/copilot-v2/Copilot component
     * Priority: useCSTWebChat > useLegacyV2Copilot > default (CopilotV3)
     */
    useLegacyV2Copilot: boolean;
}

export interface FeatureFlagsState {
    flags: CopilotFeatureFlags;
    isInitialized: boolean;
}

/**
 * Default feature flag values
 * These can be overridden at runtime via Redux actions
 */
const defaultFlags: CopilotFeatureFlags = {
    usePromptSlider: true,
    useCSTWebChat: false,
    isGroundingMenuEnabled: true,
    isPromptGalleryEnabled: true,
    isFeedbackModalEnabled: true,
    isFloatingActionButtonEnabled: true,
    isPinnedModeEnabled: true,
    isTypewritingEnabled: true,
    isSlashCommandEnabled: true,
    isAutoSuggestionEnabled: true,
    useLegacyV2Copilot: false,
};

const initialState: FeatureFlagsState = {
    flags: defaultFlags,
    isInitialized: false,
};

/**
 * Feature flags slice for copilot v2
 * Provides runtime control over copilot features
 */
const featureFlagsSlice = createSlice({
    name: 'copilotFeatureFlags',
    initialState,
    reducers: {
        /**
         * Set a single feature flag value
         */
        setFeatureFlag: (
            state,
            action: PayloadAction<{ flag: keyof CopilotFeatureFlags; value: boolean }>
        ) => {
            state.flags[action.payload.flag] = action.payload.value;
        },

        /**
         * Set multiple feature flags at once
         */
        setFeatureFlags: (
            state,
            action: PayloadAction<Partial<CopilotFeatureFlags>>
        ) => {
            state.flags = { ...state.flags, ...action.payload };
        },

        /**
         * Initialize feature flags (typically from remote config or environment)
         */
        initializeFeatureFlags: (
            state,
            action: PayloadAction<Partial<CopilotFeatureFlags>>
        ) => {
            state.flags = { ...defaultFlags, ...action.payload };
            state.isInitialized = true;
        },

        /**
         * Reset all feature flags to default values
         */
        resetFeatureFlags: (state) => {
            state.flags = defaultFlags;
            state.isInitialized = false;
        },

        /**
         * Toggle a feature flag
         */
        toggleFeatureFlag: (
            state,
            action: PayloadAction<keyof CopilotFeatureFlags>
        ) => {
            state.flags[action.payload] = !state.flags[action.payload];
        },
    },
});

export const {
    setFeatureFlag,
    setFeatureFlags,
    initializeFeatureFlags,
    resetFeatureFlags,
    toggleFeatureFlag,
} = featureFlagsSlice.actions;

export const featureFlagsReducer = featureFlagsSlice.reducer;
export default featureFlagsReducer;
